import {
  ArrowLeftOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useGoogleLogin } from "@react-oauth/google";
import { Alert, Form, Input } from "antd";
import { Button } from "components/common";
import { GoogleIcon, IconWaveHand, LogoPropone } from "components/svg";
import { configs } from "configs";
import { LOGIN_STORAGE_KEY, USER_STORAGE_KEY } from "constants";
import { getDashboardRedirectPath } from "helpers/getDashboardRedirectPath";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setAppToken, setAppUser, setIsLoggedIn } from "store/appSlice";
import { getLoginErrorMessage, updateAuthCredentials } from "utils/authHelpers";
import { inProductionMode, isBayutSa } from "utils/env";
import { ACTION_TYPES } from "utils/ga/gaEventsData";
import { fireEvent } from "utils/ga/gaHelper";
import { setStorageItem } from "utils/localStorage";
import { useGoogleLoginMutation, useSignInMutation } from "../api";
import styles from "./login.module.less";

// Check for Google OAuth availability - support both env var names
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID;
const hasGoogleOAuth = !!GOOGLE_CLIENT_ID;

const Login = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { lang } = useParams();
  const defaultLang = configs.languages[0]?.key || "en";
  const currentLang = lang || defaultLang;

  const [classicLogin, setClassicLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const hideEmailLogin = inProductionMode || isBayutSa;

  const [signIn, { isLoading: isSigningIn }] = useSignInMutation();
  const [googleLoginMutation] = useGoogleLoginMutation();

  // Google OAuth hook - same approach as Polaris
  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsGoogleLoading(true);
      setErrorMessage("");
      try {
        const result = await googleLoginMutation({
          accessToken: tokenResponse.access_token,
        });

        if (result.error) {
          handleLoginError(result.error);
        } else if (result.data) {
          handleLoginSuccess(result.data);
        }
      } catch (error) {
        handleLoginError(error);
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google OAuth error:", error);
      setErrorMessage("Google login failed. Please try again.");
      setIsGoogleLoading(false);
    },
  });

  const isLoading = isSigningIn || isGoogleLoading;

  // Handle successful login response
  const handleLoginSuccess = (response) => {
    const authHeaders = response?.authHeaders;
    const user = response?.user;

    if (user && authHeaders?.["access-token"]) {
      // Update cookies and localStorage
      updateAuthCredentials({ data: response }, authHeaders);

      // Update Redux state
      dispatch(setAppToken({ token: authHeaders["access-token"] }));
      dispatch(setAppUser({ info: user }));
      setStorageItem(USER_STORAGE_KEY, user);
      setStorageItem(LOGIN_STORAGE_KEY, "yes");
      dispatch(setIsLoggedIn(true));

      // Navigate to appropriate dashboard based on user's app access
      navigate(getDashboardRedirectPath(user));
    } else {
      setErrorMessage("Login failed. Please try again.");
    }
  };

  // Handle login error
  const handleLoginError = (error) => {
    setErrorMessage(getLoginErrorMessage(error));
  };

  // Email/Password login
  const handleSubmit = async (values) => {
    setErrorMessage("");
    try {
      const result = await signIn({
        email: values.email,
        password: values.password,
      });

      if (result.error) {
        handleLoginError(result.error);
      } else if (result.data) {
        handleLoginSuccess(result.data);
      }
    } catch (error) {
      handleLoginError(error);
    }
  };

  // Google OAuth handler - using @react-oauth/google (same as Polaris)
  const handleGoogleLogin = () => {
    if (!hasGoogleOAuth) {
      setErrorMessage(
        "Google login is not configured. Please use email login.",
      );
      return;
    }

    setErrorMessage("");
    // Trigger Google OAuth flow - the onSuccess callback will handle the rest
    loginWithGoogle();
  };

  const toggleClassicLogin = () => {
    setErrorMessage("");
    form.resetFields();
    setClassicLogin(!classicLogin);
  };

  useEffect(() => {
    fireEvent(ACTION_TYPES.login_page);
  }, []);

  return (
    <div
      className={styles.pageWrapper}
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/login-illustration.png)`,
      }}
    >
      {/* Header with Logo */}
      <div className={styles.loginHeader}>
        <LogoPropone width={228} height="auto" className={styles.logo} />
      </div>

      {/* Login Body */}
      <div className={styles.loginBody}>
        <div className={styles.loginCardWrap}>
          {/* Login Card */}
          <div
            className={`${styles.loginCard} ${
              !classicLogin ? styles.loginCardCompact : ""
            }`}
          >
            {/* Back button for classic login */}
            {classicLogin && (
              <button
                type="button"
                onClick={toggleClassicLogin}
                className={styles.backButton}
              >
                <ArrowLeftOutlined style={{ fontSize: 20, color: "#009F2B" }} />
              </button>
            )}

            {/* Header */}
            <h4 className={styles.title}>{t("Login")}</h4>
            <p className={styles.subtitle}>
              {t("Hi")} <IconWaveHand size="1.125em" />{" "}
              {t("Welcome to Propone!")}
            </p>

            {/* Login Form */}
            {classicLogin ? (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                className={styles.form}
                autoComplete="off"
              >
                <Form.Item
                  name="email"
                  label={t("Email")}
                  rules={[
                    { required: true, message: t("Please enter your email") },
                    { type: "email", message: t("Please enter a valid email") },
                  ]}
                >
                  <Input
                    placeholder={t("Email")}
                    size="large"
                    disabled={isLoading}
                    className={styles.input}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={t("Password")}
                  rules={[
                    {
                      required: true,
                      message: t("Please enter your password"),
                    },
                  ]}
                >
                  <Input.Password
                    placeholder={t("Password")}
                    size="large"
                    iconRender={(visible) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    disabled={isLoading}
                    className={styles.input}
                  />
                </Form.Item>

                <div className={styles.buttonGroup}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={isSigningIn}
                    disabled={isLoading}
                    className={styles.loginButton}
                    onClick={() => {
                      fireEvent(ACTION_TYPES.login_with_email_password);
                    }}
                  >
                    {isSigningIn ? t("Logging in...") : t("Login")}
                  </Button>
                </div>
              </Form>
            ) : (
              <div className={styles.loginOptions}>
                {/* Google Login Button */}
                <button
                  type="button"
                  onClick={() => {
                    fireEvent(ACTION_TYPES.login_with_google_click);
                    handleGoogleLogin();
                  }}
                  disabled={isLoading}
                  className={styles.googleButton}
                >
                  <GoogleIcon size={20} />
                  <span>
                    {isGoogleLoading
                      ? t("Connecting...")
                      : t("Login With Google")}
                  </span>
                </button>

                {/* Email Login Button */}
                {!hideEmailLogin && (
                  <Button
                    type="default"
                    size="large"
                    block
                    onClick={() => {
                      fireEvent(ACTION_TYPES.login_with_credentials_click);
                      toggleClassicLogin();
                    }}
                    disabled={isLoading}
                    className={styles.emailButton}
                  >
                    {t("Login With Email")}
                  </Button>
                )}
              </div>
            )}

            {/* Error Alert */}
            {errorMessage && (
              <Alert
                message={errorMessage}
                type="error"
                showIcon
                className={styles.errorAlert}
                onClose={() => setErrorMessage("")}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
//
